import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Button,
  Collapse,
  IconButton,
} from '@mui/material';
import { MonitorHeart, AccessTime, Timeline, ExpandMore, ExpandLess, Person } from '@mui/icons-material';
import { ScreenTitle } from 'components';
import { useAppSelector } from 'hooks';
import { getLocaleDate } from 'utils';
import { PartnerECGModal } from './partner-ecg-modal';
import { config } from 'packages/config';

export interface ECGRecord {
  id: string;
  createdAt: string;
  duration: number;
  ecgLead: string;
  frequency: number;
  url: string;
  patientName?: string; // Added for grouping
}

interface PartnerECGData {
  partnerName: string;
  patients: Array<{
    patientName: string;
    ecgs: ECGRecord[];
  }>;
  totalEcgs: number;
}

export const PartnerECGView: React.FC = () => {
  const userData = useAppSelector(({ user }) => user.data);
  const [ecgData, setEcgData] = useState<PartnerECGData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedECG, setSelectedECG] = useState<ECGRecord | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedPatients, setExpandedPatients] = useState<Set<string>>(new Set());

  // Extract partner info from partner data
  const partnerId = userData?.id; // This should be the partner UUID
  const partnerApiKey = (userData as any)?.apiKey;

  const processPatientData = (data: PartnerECGData) => {
    // Ensure we have a valid patients array
    if (!data?.patients || !Array.isArray(data.patients)) {
      return [];
    }

    // Process and sort ECGs within each patient
    return data.patients.map((patient) => ({
      ...patient,
      ecgs: (patient.ecgs || [])
        .map((ecg) => ({
          ...ecg,
          patientName: patient.patientName,
          url: `${config.ENV.API.EVERBEAT_BASE_URL}${ecg.url}`,
        }))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    }));
  };

  const fetchPartnerECGs = useCallback(async () => {
    if (!partnerId || !partnerApiKey) {
      setError('Partner credentials not found');
      setIsLoading(false);

      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log(`Fetching ECGs for partner: ${partnerId}`);

      const response = await fetch(
        `${config.ENV.API.EVERBEAT_BASE_URL}/partner/getEcgs/${partnerId}/${partnerApiKey}?frequency=512`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      console.log('Partner ECG response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.log('Partner ECG error response:', errorText);
        throw new Error(`Failed to fetch ECG data: ${response.status}`);
      }

      const jsonData = await response.json();
      console.log('Partner ECG JSON response:', jsonData);

      // Validate the response structure
      if (!jsonData || (!jsonData.patients && !Array.isArray(jsonData))) {
        // If the response is empty or doesn't have the expected structure,
        // create an empty data structure
        setEcgData({
          partnerName: jsonData?.partnerName || '',
          patients: [],
          totalEcgs: 0,
        });
      } else {
        setEcgData(jsonData);
      }
    } catch (err) {
      console.error('Error fetching partner ECGs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch ECG data');
    } finally {
      setIsLoading(false);
    }
  }, [partnerId, partnerApiKey]);

  useEffect(() => {
    fetchPartnerECGs();
  }, [fetchPartnerECGs]);

  const handleECGClick = useCallback((ecg: ECGRecord) => {
    setSelectedECG(ecg);
    setModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedECG(null);
  }, []);

  const formatTimestamp = useCallback((timestamp: string) => {
    return getLocaleDate(timestamp, 'MM/DD hh:mm a');
  }, []);

  const togglePatientExpanded = useCallback((patientName: string) => {
    setExpandedPatients((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(patientName)) {
        newSet.delete(patientName);
      } else {
        newSet.add(patientName);
      }

      return newSet;
    });
  }, []);

  // Get processed patient data
  const patientData = ecgData ? processPatientData(ecgData) : [];

  const renderECGContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
          <Typography variant='body1' sx={{ ml: 2 }}>
            Loading ECG data...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity='error'>
          <Typography variant='body2'>
            <strong>Error:</strong> {error}
          </Typography>
          <Typography variant='body2' sx={{ mt: 1 }}>
            Please check your partner credentials and try again.
          </Typography>
        </Alert>
      );
    }

    if (!patientData || patientData.length === 0) {
      return (
        <Alert severity='info'>
          <Typography variant='body2'>No patient ECGs connected with this partner.</Typography>
        </Alert>
      );
    }

    const totalEcgs = patientData.reduce((sum, patient) => sum + patient.ecgs.length, 0);

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <MonitorHeart sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant='h6'>
            ECG Records ({totalEcgs} total, {patientData.length} patients)
          </Typography>
        </Box>

        <List>
          {patientData.map((patient) => {
            const isExpanded = expandedPatients.has(patient.patientName);

            return (
              <Card key={patient.patientName} sx={{ mb: 2 }}>
                {/* Patient Header */}
                <ListItem
                  sx={{
                    bgcolor: 'action.hover',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                  onClick={() => togglePatientExpanded(patient.patientName)}
                >
                  <ListItemIcon>
                    <Person color='primary' />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
                          {patient.patientName}
                        </Typography>
                        <Chip label={`${patient.ecgs.length} ECGs`} size='small' color='default' variant='outlined' />
                      </Box>
                    }
                    secondary={
                      <Typography variant='body2' color='text.secondary'>
                        Latest: {patient.ecgs.length > 0 ? formatTimestamp(patient.ecgs[0].createdAt) : 'No ECGs'}
                      </Typography>
                    }
                  />
                  <IconButton>{isExpanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
                </ListItem>

                {/* ECG List */}
                <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                  <List sx={{ pl: 4, pr: 2, pb: 2 }}>
                    {patient.ecgs.map((ecg) => (
                      <ListItem key={ecg.id} sx={{ py: 1 }}>
                        <ListItemIcon>
                          <Timeline color='primary' />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Button
                                onClick={() => handleECGClick(ecg)}
                                sx={{
                                  textAlign: 'left',
                                  justifyContent: 'flex-start',
                                  textTransform: 'none',
                                  p: 0,
                                  minWidth: 'auto',
                                  fontWeight: 'bold',
                                  color: 'primary.main',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    backgroundColor: 'transparent',
                                  },
                                }}
                              >
                                {formatTimestamp(ecg.createdAt)}
                              </Button>
                              <Chip label={ecg.ecgLead} size='small' color='primary' variant='outlined' />
                              <Chip
                                label={`${Math.floor(ecg.duration)}s`}
                                size='small'
                                color='secondary'
                                variant='outlined'
                                icon={<AccessTime />}
                              />
                            </Box>
                          }
                          secondary={
                            <Typography variant='body2' color='text.secondary'>
                              Waveform ID: {ecg.id}
                            </Typography>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Card>
            );
          })}
        </List>
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <ScreenTitle title='ECG Dashboard' />

      <Box sx={{ mb: 3, mt: 2 }}>
        <Typography variant='h4' component='h1' sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {ecgData?.partnerName || (userData as any)?.name || userData?.firstName || 'Partner Company'}
        </Typography>
      </Box>

      <Box sx={{ mt: 3 }}>{renderECGContent()}</Box>

      <PartnerECGModal
        open={modalOpen}
        onClose={handleModalClose}
        ecgRecord={selectedECG}
        partnerId={partnerId}
        partnerApiKey={partnerApiKey}
      />
    </Box>
  );
};
